'use client';

/**
 * SUPPORTING PHOTOS TAB
 *
 * Shows receipt photos, document images, and other supporting photos
 * attached to finance work items (bills, invoices, purchase orders, etc.).
 *
 * Data Source: work_item_photos table
 * - Captured via SmartTask expense capture (fuel, toll, purchase receipts)
 * - Uploads from the finance document modal
 * - Includes AI receipt validation metadata
 *
 * Date: February 2026
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Camera, ImageIcon, X, Download, ZoomIn, ZoomOut, RotateCcw,
  ChevronLeft, ChevronRight, CheckCircle2, AlertCircle, Clock,
  User, Calendar, Eye, Maximize2
} from 'lucide-react';
import { authService } from '@/lib/authService';

// ============================================================================
// INTERFACES
// ============================================================================

interface SupportingPhoto {
  id: string;
  photo_url: string;
  photo_type?: string;
  caption?: string;
  ai_validated?: boolean;
  ai_confidence?: number | null;
  ai_vendor_name?: string | null;
  ai_detected_amount?: number | null;
  ai_validation_match?: string | null;
  ai_analysis_result?: any;
  uploaded_by?: number;
  uploaded_by_name?: string | null;
  created_at: string;
}

interface SupportingPhotosTabProps {
  workItemId: string;
  isActive: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString('en-ZA', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function getConfidenceStyle(confidence: number | null | undefined): {
  label: string;
  barColor: string;
  chipClass: string;
  icon: React.ElementType;
} {
  const c = confidence ?? 0;
  if (c >= 0.8) return {
    label: `${Math.round(c * 100)}% — High confidence`,
    barColor: '#22c55e',
    chipClass: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700',
    icon: CheckCircle2,
  };
  if (c >= 0.5) return {
    label: `${Math.round(c * 100)}% — Medium confidence`,
    barColor: '#f59e0b',
    chipClass: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700',
    icon: Clock,
  };
  return {
    label: `${Math.round(c * 100)}% — Low confidence`,
    barColor: '#ef4444',
    chipClass: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700',
    icon: AlertCircle,
  };
}

function getMatchChip(match: string | null | undefined): { label: string; cls: string } | null {
  if (!match) return null;
  switch (match) {
    case 'exact':   return { label: '✓ Exact match', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' };
    case 'close':   return { label: '~ Close match', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' };
    case 'no_match':
    case 'mismatch': return { label: '✗ Mismatch', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' };
    default:         return { label: match, cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' };
  }
}

// ============================================================================
// LIGHTBOX
// ============================================================================

function Lightbox({
  photos,
  initialIndex,
  onClose,
}: {
  photos: SupportingPhoto[];
  initialIndex: number;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const photo = photos[idx];

  const prev = useCallback(() => { setIdx(i => (i > 0 ? i - 1 : photos.length - 1)); setZoom(1); }, [photos.length]);
  const next = useCallback(() => { setIdx(i => (i < photos.length - 1 ? i + 1 : 0)); setZoom(1); }, [photos.length]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') onClose();
      else if (e.key === '+' || e.key === '=') setZoom(z => Math.min(z + 0.25, 4));
      else if (e.key === '-') setZoom(z => Math.max(z - 0.25, 0.5));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, onClose, prev]);

  const confidence = getConfidenceStyle(photo.ai_confidence);
  const match = getMatchChip(photo.ai_validation_match);
  const ConfIcon = confidence.icon;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-sm" onClick={onClose}>
      <div className="relative flex flex-col max-w-[95vw] max-h-[95vh] w-full" onClick={e => e.stopPropagation()}>

        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-black/60 rounded-t-lg text-white text-sm">
          <span className="font-medium">{photo.caption || `Photo ${idx + 1} of ${photos.length}`}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))} className="p-1.5 hover:bg-white/20 rounded" title="Zoom out"><ZoomOut className="w-4 h-4" /></button>
            <span className="text-xs text-white/70 w-10 text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(z + 0.25, 4))} className="p-1.5 hover:bg-white/20 rounded" title="Zoom in"><ZoomIn className="w-4 h-4" /></button>
            <a href={photo.photo_url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-white/20 rounded" title="Open original"><Maximize2 className="w-4 h-4" /></a>
            <a href={photo.photo_url} download className="p-1.5 hover:bg-white/20 rounded" title="Download"><Download className="w-4 h-4" /></a>
            <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded" title="Close"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Image area */}
        <div className="flex-1 overflow-auto flex items-center justify-center bg-black min-h-[50vh] max-h-[75vh] relative">
          {photos.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-2 z-10 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={next} className="absolute right-2 z-10 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full">
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <img
            src={photo.photo_url}
            alt={photo.caption || `Photo ${idx + 1}`}
            style={{ transform: `scale(${zoom})`, transformOrigin: 'center', transition: 'transform 0.2s' }}
            className="max-w-full max-h-full object-contain"
            draggable={false}
          />
        </div>

        {/* Bottom info bar */}
        <div className="bg-black/60 rounded-b-lg px-4 py-3 flex flex-wrap items-center gap-3 text-xs text-white/80">
          {/* Thumbnails strip */}
          {photos.length > 1 && (
            <div className="flex gap-1.5 w-full pb-2 border-b border-white/10 mb-1">
              {photos.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => { setIdx(i); setZoom(1); }}
                  className={`w-10 h-10 rounded overflow-hidden border-2 flex-shrink-0 transition-all ${i === idx ? 'border-white' : 'border-transparent opacity-60 hover:opacity-90'}`}
                >
                  <img src={p.photo_url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* AI strip */}
          {photo.ai_validated && (
            <div className={`flex flex-wrap items-center gap-2 rounded px-2 py-1 ${confidence.chipClass} border`}>
              <ConfIcon className="w-3.5 h-3.5" />
              <span className="font-semibold">{confidence.label}</span>
              {photo.ai_vendor_name && <span>· Vendor: <strong>{photo.ai_vendor_name}</strong></span>}
              {photo.ai_detected_amount != null && (
                <span>· Amount: <strong>R{Number(photo.ai_detected_amount).toFixed(2)}</strong></span>
              )}
              {match && <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${match.cls}`}>{match.label}</span>}
            </div>
          )}

          {photo.uploaded_by_name && (
            <span className="flex items-center gap-1 text-white/60"><User className="w-3 h-3" />{photo.uploaded_by_name}</span>
          )}
          <span className="flex items-center gap-1 text-white/60"><Calendar className="w-3 h-3" />{formatDate(photo.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PHOTO CARD
// ============================================================================

function PhotoCard({ photo, onClick }: { photo: SupportingPhoto; onClick: () => void }) {
  const confidence = photo.ai_validated ? getConfidenceStyle(photo.ai_confidence) : null;
  const match = getMatchChip(photo.ai_validation_match);
  const ConfIcon = confidence?.icon;

  return (
    <div className="group relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all">
      {/* Thumbnail */}
      <div className="relative aspect-[4/3] bg-gray-100 dark:bg-gray-900 cursor-pointer overflow-hidden" onClick={onClick}>
        <img
          src={photo.photo_url}
          alt={photo.caption || 'Supporting photo'}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        {/* Page number badge if multiple */}
        {photo.photo_type && (
          <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full backdrop-blur-sm">
            {photo.photo_type}
          </span>
        )}
        {/* AI validated badge */}
        {photo.ai_validated && (
          <span
            className="absolute top-2 right-2 text-xs font-bold px-1.5 py-0.5 rounded-full text-white"
            style={{ backgroundColor: confidence?.barColor }}
            title={confidence?.label}
          >
            AI {Math.round((photo.ai_confidence ?? 0) * 100)}%
          </span>
        )}
      </div>

      {/* Info section */}
      <div className="p-3 space-y-2">
        {photo.caption && (
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{photo.caption}</p>
        )}

        {/* AI confidence bar */}
        {photo.ai_validated && confidence && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                {ConfIcon && <ConfIcon className="w-3 h-3" />} AI Confidence
              </span>
              <span className="font-semibold" style={{ color: confidence.barColor }}>
                {Math.round((photo.ai_confidence ?? 0) * 100)}%
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${(photo.ai_confidence ?? 0) * 100}%`, backgroundColor: confidence.barColor }}
              />
            </div>
          </div>
        )}

        {/* AI details */}
        {photo.ai_validated && (
          <div className="flex flex-wrap gap-1.5 text-xs">
            {photo.ai_vendor_name && (
              <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded-full truncate max-w-full">
                🏪 {photo.ai_vendor_name}
              </span>
            )}
            {photo.ai_detected_amount != null && (
              <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">
                R{Number(photo.ai_detected_amount).toFixed(2)}
              </span>
            )}
            {match && (
              <span className={`px-2 py-0.5 rounded-full font-medium ${match.cls}`}>
                {match.label}
              </span>
            )}
          </div>
        )}

        {/* Meta */}
        <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500 pt-1 border-t border-gray-100 dark:border-gray-700">
          {photo.uploaded_by_name ? (
            <span className="flex items-center gap-1 truncate"><User className="w-3 h-3 flex-shrink-0" />{photo.uploaded_by_name}</span>
          ) : <span />}
          <a
            href={photo.photo_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="flex items-center gap-1 hover:text-blue-500 transition-colors flex-shrink-0"
            title="Open original"
          >
            <Eye className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function SupportingPhotosTab({ workItemId, isActive }: SupportingPhotosTabProps) {
  const [photos, setPhotos] = useState<SupportingPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const hasFetched = useRef(false);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      const res = await fetch(`/api/v1/work-items/photos?work_item_id=${encodeURIComponent(workItemId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPhotos(data.data || []);
    } catch (err: any) {
      console.error('[SupportingPhotosTab] Failed to fetch photos:', err);
      setError('Failed to load photos. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    if (isActive && !hasFetched.current) {
      hasFetched.current = true;
      fetchPhotos();
    }
  }, [isActive, fetchPhotos]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden animate-pulse">
              <div className="aspect-[4/3] bg-gray-200 dark:bg-gray-700" />
              <div className="p-3 space-y-2">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-6 text-center">
        <AlertCircle className="w-10 h-10 text-red-400" />
        <p className="text-gray-600 dark:text-gray-400">{error}</p>
        <button
          onClick={fetchPhotos}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // ── Empty ────────────────────────────────────────────────────────────────
  if (photos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full">
          <ImageIcon className="w-10 h-10 text-gray-400" />
        </div>
        <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300">No supporting photos</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
          Photos captured via SmartTask (receipts, invoices, delivery notes) or uploaded directly
          will appear here.
        </p>
      </div>
    );
  }

  // ── Summary strip ────────────────────────────────────────────────────────
  const aiPhotos = photos.filter(p => p.ai_validated);
  const avgConfidence = aiPhotos.length > 0
    ? aiPhotos.reduce((sum, p) => sum + (p.ai_confidence ?? 0), 0) / aiPhotos.length
    : 0;

  return (
    <div className="space-y-4">
      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          photos={photos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* Summary banner */}
      <div className="flex flex-wrap items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 text-sm">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-blue-500" />
          <span className="font-semibold text-gray-700 dark:text-gray-300">{photos.length} photo{photos.length !== 1 ? 's' : ''}</span>
        </div>
        {aiPhotos.length > 0 && (
          <>
            <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span>{aiPhotos.length} AI-validated</span>
              <span className="font-semibold" style={{ color: getConfidenceStyle(avgConfidence).barColor }}>
                ({Math.round(avgConfidence * 100)}% avg confidence)
              </span>
            </div>
          </>
        )}
        <button
          onClick={fetchPhotos}
          className="ml-auto text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex items-center gap-1 transition-colors"
        >
          <RotateCcw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Photo grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((photo, i) => (
          <PhotoCard
            key={photo.id}
            photo={photo}
            onClick={() => setLightboxIndex(i)}
          />
        ))}
      </div>
    </div>
  );
}
